import { Link, useParams } from "react-router-dom";
import UserDetails from "./UserDetails";
import { request } from "../../../hooks/api";
import { Transaction, User } from "src/services/types";
import UserTab from "./UserTab";
import removeRedEye from "../../../assets/icons/removeRedEye.svg";
import { useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faUtensils,
  faBox,
  faSpinner,
  faExclamationCircle,
  faInbox,
} from "@fortawesome/free-solid-svg-icons";
import html2canvas from "html2canvas";
import DeliveryLocation from "../../deliverylocation/DeliveryLocation";
import WalletTab from "./WalletTab";
import AdminService from "src/services/admin.services";
import { AppLayout } from "src/components/layouts";
import FoodLoader from "src/components/Loader";
import { formatCurrency } from "src/utils";
import { format } from "date-fns";
import UserTransactions from "src/pages/finance/components/UserTransactions";

interface WalletProps {
  userId: string;
}

interface WalletData {
  balance: { amount: number; currency_code: string };
  rewards: { bonus: { registration: number } };
}

interface OrderItem {
  restaurant_id: any;
  _id: string;
  order_code: string;
  restaurant_name: string;
  subtotal: number;
  total: number;
  delivery_fee: number;
  tax: number;
  date_delivered: string;
  items: {
    meal: {
      _id: string;
      name: string;
      description: string;
      price: {
        amount: number;
        currency_code: string;
      };
      image_url?: string;
    };
    qty: number;
  }[];
  user: string;
  order_status: string;
  payment_method: string;
  pickup_time: string;
  agent: string;
  agent_delivered_order_upload: string;
  delivery_location: string;
  enroute_time: string;
  discount: {
    order: number;
    delivery: number;
  };
  voucher: number;
  created_at: string;
  updated_at: string;
  note: string;
  user_received_order_upload: string;
}

const ITEMS_PER_PAGE = 20;

const adminService = new AdminService();

const UserDetailsWrapper: React.FC = () => {
  const { userId } = useParams<{ userId: string }>();
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [addresses, setAddresses] = useState<any[]>([]);
  const [addressLoading, setAddressLoading] = useState<boolean>(false);
  const [addressError, setAddressError] = useState<string | null>(null);
  const [tab, setTab] = useState<number>(0);
  const [orders, setOrders] = useState<OrderItem[]>([]);
  const [ordersLoading, setOrdersLoading] = useState<boolean>(false);
  const [ordersError, setOrdersError] = useState<string | null>(null);
  const [selectedOrder, setSelectedOrder] = useState<OrderItem | null>(null);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [filterStatus, setFilterStatus] = useState<string>("delivered");
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [foodOrders, setFoodOrders] = useState<any[]>([]);
  const [foodOrdersLoading, setFoodOrdersLoading] = useState(false);
  const [foodOrdersError, setFoodOrdersError] = useState("");
  const [foodCurrentPage, setFoodCurrentPage] = useState(1);
  const [foodTotalPages, setFoodTotalPages] = useState(1);
  const [activeOrderType, setActiveOrderType] = useState("food");
  const [parcelOrders, setParcelOrders] = useState<any[]>([]);
  const [parcelOrdersLoading, setParcelOrdersLoading] = useState(false);
  const [parcelOrdersError, setParcelOrdersError] = useState("");
  const [parcelCurrentPage, setParcelCurrentPage] = useState(1);
  const [parcelTotalPages, setParcelTotalPages] = useState(1);
  const [userTransactions, setUserTransactions] = useState<Transaction[]>([]);
  const [transactionsLoading, setTransactionsLoading] =
    useState<boolean>(false);
  const [transactionsError, setTransactionsError] = useState<string | null>(
    null
  );

  const normalizeAddressResponse = (response: any): any[] => {
    if (!response) return [];

    if (
      typeof response === "string" &&
      response.trim().toLowerCase() === "unknown"
    ) {
      return [];
    }

    if (
      typeof response?.data === "string" &&
      response.data.trim().toLowerCase() === "unknown"
    ) {
      return [];
    }

    if (Array.isArray(response)) return response;

    if (Array.isArray(response?.data)) return response.data;

    if (Array.isArray(response?.addresses)) return response.addresses;

    if (Array.isArray(response?.data?.addresses))
      return response.data.addresses;

    if (Array.isArray(response?.data?.delivery_addresses)) {
      return response.data.delivery_addresses;
    }

    if (Array.isArray(response?.delivery_addresses)) {
      return response.delivery_addresses;
    }

    if (
      typeof response?.address === "string" &&
      response.address.trim().toLowerCase() === "unknown"
    ) {
      return [];
    }

    if (response?.address && Array.isArray(response.address)) {
      return response.address;
    }

    if (response?.address) {
      return [response.address];
    }

    return [response];
  };

  useEffect(() => {
    const fetchUserDetails = async () => {
      if (!userId) return;

      try {
        setLoading(true);
        const response = await request(
          `/users/${userId}`,
          "GET",
          undefined,
          true,
          false,
          false
        );
        setUser(response);
        setError(null);
      } catch (err) {
        setError("Failed to fetch user details");
      } finally {
        setLoading(false);
      }
    };

    const fetchUserAddresses = async () => {
      if (!userId) return;

      setAddressLoading(true);
      setAddressError(null);

      try {
        const response = await adminService.getUserAddress(userId);
        setAddresses(normalizeAddressResponse(response));
      } catch (err) {
        console.error("Failed to fetch user address:", err);
        setAddresses([]);
        setAddressError("Failed to fetch user address");
      } finally {
        setAddressLoading(false);
      }
    };

    fetchUserDetails();
    fetchUserAddresses();
  }, [userId]);

  useEffect(() => {
    if (tab === 1) {
      // Fetch orders only when "Order History" tab is selected
      const fetchUserOrders = async () => {
        setOrdersLoading(true);
        try {
          // Initialize coordinates with Nigeria's center
          let longitude: number = 8.6753; // Default Longitude for Nigeria center
          let latitude: number = 9.082; // Default Latitude for Nigeria center

          // Ensure longitude and latitude are valid numbers before proceeding
          if (isNaN(longitude) || isNaN(latitude)) {
            console.warn(
              "Invalid coordinates, using Nigeria center coordinates"
            );
            longitude = 8.6753; // Fallback to Nigeria's center longitude
            latitude = 9.082; // Fallback to Nigeria's center latitude
          }

          const maxDistance: bigint = 100000000000000n; // 1200 kilometers in meters (bigint)

          // Build query string with dynamic parameters for user orders
          const query = `/shopping/order/search?page=${currentPage}&userId=${userId}&order_status=${filterStatus}&size=${ITEMS_PER_PAGE}&longitude=${longitude}&latitude=${latitude}&max_distance=${maxDistance.toString()}`;

          const response = await request(query, "GET", {}, true, false, false);

          if (Array.isArray(response)) {
            // Filter orders based on search term
            const filteredOrders = response.filter(
              (order) =>
                order._id.toLowerCase().includes(searchTerm.toLowerCase()) ||
                order.order_code
                  .toLowerCase()
                  .includes(searchTerm.toLowerCase()) ||
                order.restaurant_name
                  .toLowerCase()
                  .includes(searchTerm.toLowerCase()) ||
                order.user.toLowerCase().includes(searchTerm.toLowerCase()) ||
                order.agent.toLowerCase().includes(searchTerm.toLowerCase())
            );

            setOrders(filteredOrders);
            setTotalPages(Math.ceil(filteredOrders.length / ITEMS_PER_PAGE));
          } else {
            throw new Error("Unexpected response format");
          }
        } catch (err: unknown) {
          // Handle error
          if (err instanceof Error) {
            console.error("Failed to fetch orders:", err.message); // Log the error message
            setOrdersError(`Failed to fetch orders: ${err.message}`); // Show the error message
          } else {
            console.error("Failed to fetch orders: Unknown error"); // Fallback for unknown error type
            setOrdersError("Failed to fetch orders: Unknown error");
          }
        } finally {
          setOrdersLoading(false);
        }
      };

      fetchUserOrders();
    }
  }, [tab, userId, filterStatus, currentPage, searchTerm]);

  const handleSearch = () => {
    setCurrentPage(1); // Reset to the first page when searching
  };

  useEffect(() => {
    const fetchUsersWallet = async () => {
      try {
        const response = await request(
          `/users/wallet/${userId}`,
          "GET",
          {},
          true,
          false,
          false
        );

        if (response && response.userId === userId) {
          return response;
        }
      } catch (error) {
        console.error("Error fetching wallet details:", error);
      }
    };

    if (userId) {
      fetchUsersWallet();
    } else {
      console.warn("userId is undefined");
    }

    const fetchUserTransactions = async () => {
      if (!userId) return;

      setTransactionsLoading(true);
      setTransactionsError(null);

      try {
        const response = await adminService.getUserTransactions(
          1,
          100,
          "",
          userId
        );
        setUserTransactions(response);
      } catch (error) {
        console.error("Error fetching user transactions:", error);
        setTransactionsError(
          error instanceof Error
            ? error.message
            : "Failed to fetch user transactions"
        );
      } finally {
        setTransactionsLoading(false);
      }
    };

    fetchUserTransactions();
  }, [userId]); // Add userId as a dependency

  const handleDownload = () => {
    const csvRows = [
      [
        "Order ID",
        "Order Code",
        "Restaurant",
        "Subtotal",
        "Total",
        "Delivery Fee",
        "Tax",
        "Date Delivered",
        "Items",
        "User",
        "Order Status",
        "Payment Method",
        "Pickup Time",
        "Agent",
        "Agent Delivered Order Upload",
        "Delivery Location",
        "Enroute Time",
        "Discount Order",
        "Discount Delivery",
        "Voucher",
        "Created At",
        "Updated At",
        "Note",
        "User Received Order Upload",
      ], // CSV header
      ...orders.map((order) => [
        order._id,
        order.order_code,
        order.restaurant_name,
        order.subtotal,
        order.total,
        order.delivery_fee,
        order.tax,

        order.items.map((item) => `${item.meal.name} (${item.qty})`).join(", "),
        order.user,
        order.order_status,
        order.payment_method,

        order.agent,
        order.agent_delivered_order_upload,
        order.delivery_location,
        // Check for default/invalid dates before formatting
        new Date(order.date_delivered).getFullYear() !== 1
          ? new Date(order.date_delivered).toLocaleDateString()
          : "",
        new Date(order.pickup_time).getFullYear() !== 1
          ? new Date(order.pickup_time).toLocaleDateString()
          : "",
        new Date(order.enroute_time).getFullYear() !== 1
          ? new Date(order.enroute_time).toLocaleDateString()
          : "",
        new Date(order.created_at).getFullYear() !== 1
          ? new Date(order.created_at).toLocaleDateString()
          : "",

        order.agent,
        order.agent_delivered_order_upload,
        order.delivery_location,
        order.discount?.order || "", // Handle missing discount
        order.discount?.delivery || "", // Handle missing delivery discount
        order.voucher || "", // Handle missing voucher

        order.note || "", // Handle missing notes
        order.user_received_order_upload || "", // Handle missing upload
      ]),
    ];

    const csvContent =
      "data:text/csv;charset=utf-8," +
      csvRows.map((e) => e.join(",")).join("\n");
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", "orders.csv");
    document.body.appendChild(link);
    link.click();
  };

  const downloadInvoice = () => {
    const captureElement = document.getElementById("order-details");

    if (captureElement && selectedOrder) {
      // Added null check for selectedOrder
      html2canvas(captureElement, {
        useCORS: true,
        scale: 2, // Increase the scale to improve image quality
        logging: true, // Enable logging to check any issues
      })
        .then((canvas) => {
          const link = document.createElement("a");
          link.href = canvas.toDataURL("image/png");
          link.download = `Order_${selectedOrder._id}.png`; // selectedOrder is now guaranteed to be non-null
          link.click();
        })
        .catch((error) => {
          console.error("Error capturing the element:", error);
        });
    } else {
      if (!captureElement) {
        console.error("Capture element is missing");
      }
      if (!selectedOrder) {
        console.error("Selected order is missing");
      }
    }
  };

  const openModal = (order: OrderItem) => {
    setSelectedOrder(order);
  };

  const closeModal = () => {
    setSelectedOrder(null);
  };

  const handlePageChange = (page: number) => {
    if (page > 0 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const fetchFoodOrders = async () => {
    setFoodOrdersLoading(true);
    try {
      const response = await adminService.getOrders(
        1,
        100,
        "",
        "all",
        7.4951,
        9.0578,
        userId as string,
        100000000000n
      );
      setFoodOrders(response);
      setFoodTotalPages(response.length);
    } catch (error: any) {
      setFoodOrdersError(error.message);
    } finally {
      setFoodOrdersLoading(false);
    }
  };

  const fetchParcelOrders = async () => {
    setParcelOrdersLoading(true);
    try {
      const response = await adminService.getLogisticsManagement(
        1,
        7.4951,
        9.0578,
        100000000000n,
        "all",
        userId as string
      );
      setParcelOrders(response);
      setParcelTotalPages(response.length);
    } catch (error: any) {
      setParcelOrdersError(error.message);
    } finally {
      setParcelOrdersLoading(false);
    }
  };

  const handleFoodPreviousPage = () => {
    if (foodCurrentPage > 1) {
      setFoodCurrentPage(foodCurrentPage - 1);
      fetchFoodOrders();
    }
  };

  const handleFoodNextPage = () => {
    if (foodCurrentPage < foodTotalPages) {
      setFoodCurrentPage(foodCurrentPage + 1);
      fetchFoodOrders();
    }
  };

  const handleParcelPreviousPage = () => {
    if (parcelCurrentPage > 1) {
      setParcelCurrentPage(parcelCurrentPage - 1);
      fetchParcelOrders();
    }
  };

  const handleParcelNextPage = () => {
    if (parcelCurrentPage < parcelTotalPages) {
      setParcelCurrentPage(parcelCurrentPage + 1);
      fetchParcelOrders();
    }
  };

  if (loading)
    return (
      <div className="h-[70vh] w-full bg-white flex flex-col items-center justify-center">
        <FoodLoader
          size="md"
          text="Fetching User"
          theme="amber"
          className="scale-75"
        />
      </div>
    );
  if (error) return <div className="text-center text-red-500">{error}</div>;

  return (
    <AppLayout>
      <div className="max-w-4xl mx-auto p-4">
        <UserTab setTab={setTab} tab={tab} />
        <div className="mt-6">
          {tab === 0 && user ? (
            <UserDetails
              user={user}
              addresses={addresses}
              addressLoading={addressLoading}
              addressError={addressError}
              onStatusChange={(newStatus: string) => {
                setUser((prev) =>
                  prev
                    ? {
                        ...prev,
                        is_suspended: newStatus === "suspended",
                      }
                    : prev
                );
              }}
            />
          ) : tab === 1 ? (
            <div className="mt-6">
              {/* <div className="mb-6 flex flex-col md:flex-row items-start md:items-center justify-between gap-4 bg-white p-4 rounded-lg shadow-sm">
              <div className="relative w-full md:w-72">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <FontAwesomeIcon icon={faSearch} className="text-gray-400" />
                </div>
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onKeyPress={(e) => e.key === "Enter" && handleSearch()}
                  placeholder="Search orders..."
                  className="block w-full pl-10 pr-3 py-2.5 border border-gray-200 rounded-lg bg-gray-50 focus:bg-white focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-amber-500 transition-all"
                />
              </div>

              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 w-full md:w-auto">
                <div className="relative w-full sm:w-48">
                  <select
                    value={filterStatus}
                    onChange={(e) => setFilterStatus(e.target.value)}
                    className="block w-full px-4 py-2.5 border border-gray-200 rounded-lg bg-gray-50 focus:bg-white focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-amber-500 appearance-none"
                  >
                    <option value="all">All Statuses</option>
                    <option value="delivered">Delivered</option>
                    <option value="pending">Pending</option>
                    <option value="cancelled">Cancelled</option>
                    <option value="confirmed">Confirmed</option>
                    <option value="declined">Declined</option>
                  </select>
                  <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                    <FontAwesomeIcon
                      icon={faChevronDown}
                      className="text-gray-400 text-sm"
                    />
                  </div>
                </div>

                <button
                  onClick={handleDownload}
                  className="flex items-center justify-center gap-2 px-4 py-2.5 border border-transparent rounded-lg bg-amber-500 text-white hover:bg-amber-600 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:ring-offset-2 transition-all whitespace-nowrap w-full sm:w-auto"
                >
                  <FontAwesomeIcon icon={faDownload} />
                  Download CSV
                </button>
              </div>
            </div> */}

              {/* Order Type Tabs */}
              <div className="flex border-b mb-6">
                <button
                  className={`py-3 px-6 font-medium text-sm flex items-center gap-2 ${
                    activeOrderType === "food"
                      ? "border-b-2 border-amber-500 text-amber-600"
                      : "text-gray-500 hover:text-gray-700"
                  }`}
                  onClick={() => {
                    setActiveOrderType("food");
                    if (!foodOrders.length && !foodOrdersLoading) {
                      fetchFoodOrders();
                    }
                  }}
                >
                  <FontAwesomeIcon icon={faUtensils} className="text-sm" />
                  Food Orders
                </button>
                <button
                  className={`py-3 px-6 font-medium text-sm flex items-center gap-2 ${
                    activeOrderType === "parcel"
                      ? "border-b-2 border-amber-500 text-amber-600"
                      : "text-gray-500 hover:text-gray-700"
                  }`}
                  onClick={() => {
                    setActiveOrderType("parcel");
                    if (!parcelOrders.length && !parcelOrdersLoading) {
                      fetchParcelOrders();
                    }
                  }}
                >
                  <FontAwesomeIcon icon={faBox} className="text-sm" />
                  Parcel Orders
                </button>
              </div>

              {/* Orders Table Section */}
              <div className="bg-white rounded-lg shadow-sm overflow-hidden">
                {activeOrderType === "food" ? (
                  <>
                    {foodOrdersLoading ? (
                      <div className="p-8 text-center text-gray-500">
                        <FontAwesomeIcon
                          icon={faSpinner}
                          spin
                          className="mr-2"
                        />
                        Loading food orders...
                      </div>
                    ) : foodOrdersError ? (
                      <div className="p-8 text-center text-red-500">
                        <FontAwesomeIcon
                          icon={faExclamationCircle}
                          className="mr-2"
                        />
                        {foodOrdersError}
                      </div>
                    ) : foodOrders.length ? (
                      <div className="overflow-x-auto">
                        <table className="min-w-full text-sm text-left">
                          <thead className="bg-amber-100/70 text-left text-xs font-semibold text-amber-900 uppercase tracking-wider">
                            <tr>
                              <th className="px-5 py-3 border-b border-amber-200 whitespace-nowrap">
                                Order ID
                              </th>
                              <th className="px-5 py-3 border-b border-amber-200 whitespace-nowrap">
                                Order Code
                              </th>
                              <th className="px-5 py-3 border-b border-amber-200 whitespace-nowrap">
                                Restaurant
                              </th>
                              <th className="px-5 py-3 border-b border-amber-200 whitespace-nowrap">
                                Subtotal
                              </th>
                              <th className="px-5 py-3 border-b border-amber-200 whitespace-nowrap">
                                Total
                              </th>
                              <th className="px-5 py-3 border-b border-amber-200 whitespace-nowrap">
                                Date Delivered
                              </th>
                              {/* <th className="px-5 py-3 border-b border-amber-200 whitespace-nowrap">
                              Actions
                            </th> */}
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {foodOrders.map((order) => (
                              <tr
                                key={order._id}
                                className="hover:bg-amber-50/80"
                              >
                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                  {order._id}
                                </td>
                                <td className="px-5 py-3 border-b border-amber-100">
                                  {order.order_code}
                                </td>
                                <td className="px-5 py-3 border-b border-amber-100">
                                  {order.restaurant_name}
                                </td>
                                <td className="px-5 py-3 border-b border-amber-100">
                                  {formatCurrency(order.subtotal)}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-medium">
                                  {formatCurrency(order.total)}
                                </td>
                                <td className="px-5 py-3 border-b border-amber-100 whitespace-nowrap">
                                  {format(order.date_delivered, "PPpp")}
                                </td>
                                {/* <td className="px-5 py-3 border-b border-amber-100">
                                <button
                                  onClick={() => openModal(order)}
                                  className="text-amber-600 hover:text-amber-800 flex items-center gap-1"
                                >
                                  <FontAwesomeIcon icon={faEye} />
                                  <span>View</span>
                                </button>
                              </td> */}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <div className="p-8 text-center text-gray-500">
                        <FontAwesomeIcon icon={faInbox} className="mr-2" />
                        No food orders found
                      </div>
                    )}
                  </>
                ) : (
                  <>
                    {parcelOrdersLoading ? (
                      <div className="p-8 text-center text-gray-500">
                        <FontAwesomeIcon
                          icon={faSpinner}
                          spin
                          className="mr-2"
                        />
                        Loading parcel orders...
                      </div>
                    ) : parcelOrdersError ? (
                      <div className="p-8 text-center text-red-500">
                        <FontAwesomeIcon
                          icon={faExclamationCircle}
                          className="mr-2"
                        />
                        {parcelOrdersError}
                      </div>
                    ) : parcelOrders.length ? (
                      <div className="overflow-x-auto">
                        <table className="min-w-full text-sm text-left">
                          <thead className="bg-amber-100/70 text-left text-xs font-semibold text-amber-900 uppercase tracking-wider">
                            <tr>
                              <th className="px-5 py-3 border-b border-amber-200 whitespace-nowrap">
                                Order ID
                              </th>
                              <th className="px-5 py-3 border-b border-amber-200 whitespace-nowrap">
                                Tracking
                              </th>
                              <th className="px-5 py-3 border-b border-amber-200 whitespace-nowrap">
                                Pickup Location
                              </th>
                              <th className="px-5 py-3 border-b border-amber-200 whitespace-nowrap">
                                Delivery Location
                              </th>
                              <th className="px-5 py-3 border-b border-amber-200 whitespace-nowrap">
                                Total
                              </th>
                              <th className="px-5 py-3 border-b border-amber-200 whitespace-nowrap">
                                Status
                              </th>
                              {/* <th className="px-5 py-3 border-b border-amber-200 whitespace-nowrap">
                              Actions
                            </th> */}
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {parcelOrders.map((order) => (
                              <tr key={order._id} className="hover:bg-gray-50">
                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                  {order._id}
                                </td>
                                <td className="px-5 py-3 border-b border-amber-100">
                                  {order.trackingId}
                                </td>
                                <td className="px-5 py-3 border-b border-amber-100 text-sm text-gray-500 max-w-xs truncate">
                                  {order?.pickup_location?.address || "—"}
                                </td>
                                <td className="px-5 py-3 border-b border-amber-100 text-sm text-gray-500 max-w-xs truncate">
                                  {order?.receiver?.address?.address || "—"}
                                </td>
                                <td className="px-5 py-3 border-b border-amber-100 whitespace-nowrap text-sm text-gray-900 font-medium">
                                  {formatCurrency(order.total)}
                                </td>
                                <td className="px-5 py-3 border-b border-amber-100 whitespace-nowrap">
                                  <span
                                    className={`px-2 py-1 rounded-full text-xs font-medium ${
                                      order.order_status === "delivered"
                                        ? "bg-green-100 text-green-800"
                                        : order.order_status === "pending"
                                        ? "bg-yellow-100 text-yellow-800"
                                        : "bg-red-100 text-red-800"
                                    }`}
                                  >
                                    {order.order_status}
                                  </span>
                                </td>
                                {/* <td className="px-5 py-3 border-b border-amber-100 whitespace-nowrap text-sm text-gray-500">
                                <button
                                  onClick={() => openParcelModal(order)}
                                  className="text-amber-600 hover:text-amber-800 flex items-center gap-1"
                                >
                                  <FontAwesomeIcon icon={faEye} />
                                  <span>View</span>
                                </button>
                              </td> */}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <div className="p-8 text-center text-gray-500">
                        <FontAwesomeIcon icon={faInbox} className="mr-2" />
                        No parcel orders found
                      </div>
                    )}
                  </>
                )}

                {/* Pagination */}
                {(foodOrders.length > 0 || parcelOrders.length > 0) && (
                  <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex items-center justify-between">
                    <button
                      onClick={
                        activeOrderType === "food"
                          ? handleFoodPreviousPage
                          : handleParcelPreviousPage
                      }
                      disabled={
                        activeOrderType === "food"
                          ? foodCurrentPage === 1
                          : parcelCurrentPage === 1
                      }
                      className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Previous
                    </button>
                    <span className="text-sm text-gray-700">
                      Page{" "}
                      {activeOrderType === "food"
                        ? foodCurrentPage
                        : parcelCurrentPage}{" "}
                      of{" "}
                      {activeOrderType === "food"
                        ? foodTotalPages
                        : parcelTotalPages}
                    </span>
                    <button
                      onClick={
                        activeOrderType === "food"
                          ? handleFoodNextPage
                          : handleParcelNextPage
                      }
                      disabled={
                        activeOrderType === "food"
                          ? foodCurrentPage === foodTotalPages
                          : parcelCurrentPage === parcelTotalPages
                      }
                      className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Next
                    </button>
                  </div>
                )}
              </div>
            </div>
          ) : null}
        </div>

        {selectedOrder && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
            <div
              id="order-details"
              className="bg-white p-6 rounded-lg shadow-lg max-w-lg w-full relative max-h-screen overflow-y-auto"
            >
              <button
                onClick={closeModal}
                className="absolute top-4 right-4 bg-yellow-500 text-white py-2 px-4 rounded"
              >
                Close
              </button>
              <h2 className="text-2xl font-bold mb-4">Order Details</h2>

              <div className="space-y-2">
                <div className="flex justify-between border-b pb-2">
                  <p>
                    <strong>Order ID:</strong>
                  </p>
                  <p>{selectedOrder._id}</p>
                </div>
                <div className="flex justify-between border-b pb-2">
                  <p>
                    <strong>Order Code:</strong>
                  </p>
                  <p>{selectedOrder.order_code}</p>
                </div>

                <div className="flex justify-between border-b pb-2">
                  <p>
                    <strong>Date Delivered:</strong>
                  </p>
                  <p>
                    {selectedOrder.date_delivered &&
                    new Date(selectedOrder.date_delivered).getFullYear() !== 1
                      ? `${new Date(
                          selectedOrder.date_delivered
                        ).toLocaleDateString()} ${new Date(
                          selectedOrder.date_delivered
                        ).toLocaleTimeString()}`
                      : ""}
                  </p>
                </div>
                <div className="flex justify-between border-b pb-2">
                  <p>
                    <strong>pickup time</strong>
                  </p>
                  <p>
                    {selectedOrder.pickup_time &&
                    new Date(selectedOrder.pickup_time).getFullYear() !== 1
                      ? `${new Date(
                          selectedOrder.pickup_time
                        ).toLocaleDateString()} ${new Date(
                          selectedOrder.pickup_time
                        ).toLocaleTimeString()}`
                      : ""}
                  </p>
                </div>

                <div className="flex justify-between border-b pb-2">
                  <p>
                    <strong>Order Date:</strong>
                  </p>
                  <p>
                    {selectedOrder.created_at
                      ? `${new Date(
                          selectedOrder.created_at
                        ).toLocaleDateString()} ${new Date(
                          selectedOrder.created_at
                        ).toLocaleTimeString()}`
                      : " "}
                  </p>
                </div>

                <div className="flex justify-between border-b pb-2">
                  <p>
                    <strong>created At</strong>
                  </p>
                  <p>
                    {selectedOrder.created_at &&
                    new Date(selectedOrder.created_at).getFullYear() !== 1
                      ? `${new Date(
                          selectedOrder.created_at
                        ).toLocaleDateString()} ${new Date(
                          selectedOrder.created_at
                        ).toLocaleTimeString()}`
                      : ""}
                  </p>
                </div>

                <div className="flex justify-between border-b pb-2">
                  <p>
                    <strong>Enroute Time</strong>
                  </p>
                  <p>
                    {selectedOrder.enroute_time &&
                    new Date(selectedOrder.enroute_time).getFullYear() !== 1
                      ? `${new Date(
                          selectedOrder.enroute_time
                        ).toLocaleDateString()} ${new Date(
                          selectedOrder.enroute_time
                        ).toLocaleTimeString()}`
                      : ""}
                  </p>
                </div>

                <div className="flex justify-between border-b pb-2">
                  <p>
                    <strong>Order Status:</strong>
                  </p>
                  <p>{selectedOrder.order_status}</p>
                </div>
                <div className="flex justify-between border-b pb-2">
                  <p>
                    <strong>Payment Method:</strong>
                  </p>
                  <p>{selectedOrder.payment_method}</p>
                </div>
                <div className="flex justify-between border-b pb-2">
                  <p>
                    <strong>Agent:</strong>
                  </p>
                  <Link
                    to={`/pr-admin/agents/agent-details/${selectedOrder.agent}`}
                  >
                    <img src={removeRedEye} alt="View" />
                  </Link>
                </div>
                <div className="flex justify-between border-b pb-2">
                  <p>
                    <strong>User:</strong>
                  </p>

                  <Link
                    to={`/pr-admin/users/user-details/${selectedOrder.user}`}
                  >
                    <img src={removeRedEye} alt="View" />
                  </Link>
                </div>

                <div className="flex justify-between border-b pb-2">
                  <p>
                    <strong>Agent Delivered Order Upload:</strong>
                  </p>
                  <p>{selectedOrder.agent_delivered_order_upload}</p>
                </div>
                <div className="flex justify-between border-b pb-2">
                  {selectedOrder && (
                    <DeliveryLocation
                      locationId={selectedOrder.delivery_location as string}
                    />
                  )}
                </div>

                <div className="flex justify-between border-b pb-2">
                  <p>
                    <strong>Discount (Order):</strong>
                  </p>
                  <p>₦{selectedOrder.discount.order}</p>
                </div>
                <div className="flex justify-between border-b pb-2">
                  <p>
                    <strong>Discount (Delivery):</strong>
                  </p>
                  <p>₦{selectedOrder.discount.delivery}</p>
                </div>
                <div className="flex justify-between border-b pb-2">
                  <p>
                    <strong>Voucher:</strong>
                  </p>
                  <p>₦{selectedOrder.voucher}</p>
                </div>

                <div className="flex justify-between border-b pb-2">
                  <p>
                    <strong>Note:</strong>
                  </p>
                  <p>{selectedOrder.note}</p>
                </div>
                <div className="flex justify-between border-b pb-2">
                  <p>
                    <strong>User Received Order Upload:</strong>
                  </p>
                  <p>{selectedOrder.user_received_order_upload}</p>
                </div>
              </div>

              <div className="mt-4">
                <h3 className="text-lg font-semibold mb-2">Items:</h3>
                <ul>
                  {selectedOrder.items.map((item) => (
                    <li key={item.meal._id} className="mb-2">
                      <div className="flex justify-between border-b pb-2">
                        <p>
                          <strong>Meal Name:</strong>
                        </p>
                        <p>{item.meal.name}</p>
                      </div>
                      <div className="flex justify-between border-b pb-2">
                        <p>
                          <strong>Description:</strong>
                        </p>
                        <p>{item.meal.description}</p>
                      </div>
                      <div className="flex justify-between border-b pb-2">
                        <p>
                          <strong>Price:</strong>
                        </p>
                        <p>₦{item.meal.price.amount} </p>
                      </div>
                      {item.meal.image_url && (
                        <img
                          src={item.meal.image_url}
                          alt={item.meal.name}
                          className="w-16 h-16 object-cover mt-2"
                        />
                      )}
                      <div className="flex justify-between border-b pb-2">
                        <p>
                          <strong>Quantity:</strong>
                        </p>
                        <p>{item.qty}</p>
                      </div>
                    </li>
                  ))}
                </ul>
                <div className="flex justify-between border-b pb-2">
                  <p>
                    <strong>Restaurant:</strong>
                  </p>
                  <p>{selectedOrder.restaurant_name}</p>
                </div>
                <div className="flex justify-between border-b pb-2">
                  <p>
                    <strong>Subtotal:</strong>
                  </p>
                  <p>₦{selectedOrder.subtotal}</p>
                </div>

                <div className="flex justify-between border-b pb-2">
                  <p>
                    <strong>Delivery Fee:</strong>
                  </p>
                  <p>₦{selectedOrder.delivery_fee}</p>
                </div>
                <div className="flex justify-between border-b pb-2">
                  <p>
                    <strong>Service Charge:</strong>
                  </p>
                  <p>₦{selectedOrder.tax}</p>
                </div>
              </div>

              <div className="flex justify-between border-b pb-2">
                <p>
                  <strong>Total:</strong>
                </p>
                <p>₦{selectedOrder.total}</p>
              </div>

              <button
                onClick={downloadInvoice}
                className="mt-4 bg-green-500 text-white py-2 px-4 rounded"
              >
                Download Invoice
              </button>
            </div>
          </div>
        )}

        {/* Conditionally render WalletTab based on the selected tab */}
        {tab === 2 && userId && (
          <div className="bg-white p-6 rounded-lg shadow">
            <WalletTab
              usersId={userId} // Ensure you're passing the correct variable
            />{" "}
            {/* Display wallet data for selected agent */}
          </div>
        )}

        {tab === 3 && userId && (
          <UserTransactions
            transactions={userTransactions}
            loading={transactionsLoading}
            error={transactionsError}
          />
        )}
      </div>
    </AppLayout>
  );
};

export default UserDetailsWrapper;
function fetchUsersWallet() {
  throw new Error("Function not implemented.");
}
